/**
 * Shop back office API — gateway /api/admin/shop (WooCommerce proxy + campaigns).
 * Uses the same JWT storage as the member portal (marple_member_token).
 */

import type { AuthTokens } from '@/types/member';
import { getAuthToken, setAuthTokens } from '@/services/memberApi';

const RAW_API_URL = import.meta.env.VITE_API_URL || '';
const NORMALIZED_API_URL = RAW_API_URL.replace(/\/$/, '');

export function shopAdminApiRoot(): string {
  if (!NORMALIZED_API_URL) return '/api/admin/shop';
  return NORMALIZED_API_URL === '/api'
    ? '/api/admin/shop'
    : `${NORMALIZED_API_URL}/api/admin/shop`;
}

export async function shopAdminLogin(email: string, password: string): Promise<AuthTokens & { shop_backoffice_access?: boolean }> {
  const res = await fetch(`${shopAdminApiRoot()}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = data.detail;
    const msg = typeof detail === 'string' ? detail : 'Login failed';
    throw Object.assign(new Error(msg), { status: res.status });
  }
  setAuthTokens(data);
  return data;
}

export async function shopAdminFetch<T = unknown>(
  path: string,
  options: RequestInit = {}
): Promise<T | null> {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers as Record<string, string> | undefined),
  };
  const res = await fetch(`${shopAdminApiRoot()}${path}`, {
    ...options,
    headers,
  });
  if (res.status === 204) return null;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = data.detail;
    let msg = 'Request failed';
    if (typeof detail === 'string') msg = detail;
    else if (detail && typeof detail === 'object') msg = JSON.stringify(detail);
    throw Object.assign(new Error(msg), { status: res.status });
  }
  return data as T;
}

export async function shopAdminMe(): Promise<{
  id: string;
  email: string;
  first_name: string;
  surname: string;
  shop_backoffice_access: boolean;
}> {
  return shopAdminFetch('/me') as Promise<{
    id: string;
    email: string;
    first_name: string;
    surname: string;
    shop_backoffice_access: boolean;
  }>;
}

// ── Banners ──────────────────────────────────────────────────────────────────

export interface ShopBanner {
  id: string;
  title: string;
  subtitle: string | null;
  link_url: string | null;
  background_color: string;
  text_color: string;
  position: string;
  priority: number;
  is_active: boolean;
  starts_at: string | null;
  ends_at: string | null;
  created_at: string;
  updated_at: string;
}

export type ShopBannerInput = Omit<ShopBanner, 'id' | 'created_at' | 'updated_at'>;

export async function listBanners(): Promise<ShopBanner[]> {
  return shopAdminFetch('/banners') as Promise<ShopBanner[]>;
}

export async function createBanner(data: ShopBannerInput): Promise<ShopBanner> {
  return shopAdminFetch('/banners', { method: 'POST', body: JSON.stringify(data) }) as Promise<ShopBanner>;
}

export async function updateBanner(id: string, data: Partial<ShopBannerInput>): Promise<ShopBanner> {
  return shopAdminFetch(`/banners/${id}`, { method: 'PUT', body: JSON.stringify(data) }) as Promise<ShopBanner>;
}

export async function deleteBanner(id: string): Promise<null> {
  return shopAdminFetch(`/banners/${id}`, { method: 'DELETE' });
}

// ── Splash screens ───────────────────────────────────────────────────────────

export type SplashFrequency = 'once_per_session' | 'every_visit';

export interface ShopSplashScreen {
  id: string;
  title: string;
  body: string | null;
  image_url: string | null;
  cta_text: string | null;
  cta_link: string | null;
  background_color: string;
  text_color: string;
  display_frequency: SplashFrequency;
  delay_seconds: number;
  is_active: boolean;
  starts_at: string | null;
  ends_at: string | null;
  created_at: string;
  updated_at: string;
}

export type ShopSplashScreenInput = Omit<ShopSplashScreen, 'id' | 'created_at' | 'updated_at'>;

export async function listSplashScreens(): Promise<ShopSplashScreen[]> {
  return shopAdminFetch('/splash-screens') as Promise<ShopSplashScreen[]>;
}

export async function createSplashScreen(data: ShopSplashScreenInput): Promise<ShopSplashScreen> {
  return shopAdminFetch('/splash-screens', { method: 'POST', body: JSON.stringify(data) }) as Promise<ShopSplashScreen>;
}

export async function updateSplashScreen(id: string, data: Partial<ShopSplashScreenInput>): Promise<ShopSplashScreen> {
  return shopAdminFetch(`/splash-screens/${id}`, { method: 'PUT', body: JSON.stringify(data) }) as Promise<ShopSplashScreen>;
}

export async function deleteSplashScreen(id: string): Promise<null> {
  return shopAdminFetch(`/splash-screens/${id}`, { method: 'DELETE' });
}

// ── Discounts ────────────────────────────────────────────────────────────────

export type DiscountType = 'percent' | 'fixed' | 'free_shipping';
export type AppliesTo = 'all' | 'products' | 'categories';

export interface ShopDiscount {
  id: string;
  code: string;
  description: string | null;
  discount_type: DiscountType;
  value: number;
  min_order_amount: number | null;
  max_discount_amount: number | null;
  applies_to: AppliesTo;
  product_ids: number[];
  category_ids: number[];
  usage_limit: number | null;
  usage_count: number;
  is_active: boolean;
  starts_at: string | null;
  ends_at: string | null;
  created_at: string;
  updated_at: string;
}

export type ShopDiscountInput = Omit<ShopDiscount, 'id' | 'usage_count' | 'created_at' | 'updated_at'>;

export async function listDiscounts(): Promise<ShopDiscount[]> {
  return shopAdminFetch('/discounts') as Promise<ShopDiscount[]>;
}

export async function createDiscount(data: ShopDiscountInput): Promise<ShopDiscount> {
  return shopAdminFetch('/discounts', { method: 'POST', body: JSON.stringify(data) }) as Promise<ShopDiscount>;
}

export async function updateDiscount(id: string, data: Partial<ShopDiscountInput>): Promise<ShopDiscount> {
  return shopAdminFetch(`/discounts/${id}`, { method: 'PUT', body: JSON.stringify(data) }) as Promise<ShopDiscount>;
}

export async function deleteDiscount(id: string): Promise<null> {
  return shopAdminFetch(`/discounts/${id}`, { method: 'DELETE' });
}

// ── Shipping rules ───────────────────────────────────────────────────────────

export interface ShopShippingRule {
  id: string;
  name: string;
  zone: string | null;
  suburb_pattern: string | null;
  method: string;
  base_rate: number;
  free_threshold: number | null;
  weight_rate_per_kg: number | null;
  min_days: number | null;
  max_days: number | null;
  is_active: boolean;
  priority: number;
  created_at: string;
  updated_at: string;
}

export type ShopShippingRuleInput = Omit<ShopShippingRule, 'id' | 'created_at' | 'updated_at'>;

export async function listShippingRules(): Promise<ShopShippingRule[]> {
  return shopAdminFetch('/shipping-rules') as Promise<ShopShippingRule[]>;
}

export async function createShippingRule(data: ShopShippingRuleInput): Promise<ShopShippingRule> {
  return shopAdminFetch('/shipping-rules', { method: 'POST', body: JSON.stringify(data) }) as Promise<ShopShippingRule>;
}

export async function updateShippingRule(id: string, data: Partial<ShopShippingRuleInput>): Promise<ShopShippingRule> {
  return shopAdminFetch(`/shipping-rules/${id}`, { method: 'PUT', body: JSON.stringify(data) }) as Promise<ShopShippingRule>;
}

export async function deleteShippingRule(id: string): Promise<null> {
  return shopAdminFetch(`/shipping-rules/${id}`, { method: 'DELETE' });
}
